import ContactApi from '../../apis/contact';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { errorHandler } from '../../helper/error_handler';
import { isErrorWithMessage } from '../../helper';
import { RootState } from '../../redux/store';
import { AddContactParams, DeleteContactParams, EditContactParams } from '../../params/redux/contact';
import { resettingContactLoader, settingSelectedContact } from '../slices/contact';


export const fetchDealContactThunk = createAsyncThunk(
    'contact/fetch',
    async (_, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            if (state.deal.selectedDeal !== null) {
                const response = await ContactApi.fetchContacts(state.app.token, state.deal.selectedDeal?.id as number);
                if (response?.success === 1) {
                    return response?.deal_contacts;
                }
                return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
            }
            return [];
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingContactLoader());
        }
    }
);

export const createContactThunk = createAsyncThunk(
    'contact/create',
    async (data: AddContactParams, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await ContactApi.createContact(state.app.token, data);
            if (response?.success === 1) {
                await dispatch(fetchDealContactThunk()).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingContactLoader());
        }
    }
);

export const editContactThunk = createAsyncThunk(
    'contact/edit',
    async (data: EditContactParams, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await ContactApi.editContact(state.app.token, data);
            if (response?.success === 1) {
                dispatch(settingSelectedContact(null));
                await dispatch(fetchDealContactThunk()).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingContactLoader());
        }
    }
);

export const deleteContactThunk = createAsyncThunk(
    'contact/delete',
    async (data: DeleteContactParams, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await ContactApi.deleteContact(state.app.token, data);
            if (response?.success === 1) {
                dispatch(settingSelectedContact(null));
                await dispatch(fetchDealContactThunk()).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingContactLoader());
        }
    }
);

export const fetchClientContactThunk = createAsyncThunk(
    'contact/fetch-client',
    async (client_id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await ContactApi.fetchClientContacts(state.app.token, client_id);
            if (response?.success === 1) {
                return response?.client_contacts;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingContactLoader());
        }
    }
);

export const createClientContactThunk = createAsyncThunk(
    'contact/create-client',
    async (data: AddContactParams, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await ContactApi.createClientContact(state.app.token, data);
            if (response?.success === 1) {
                await dispatch(fetchClientContactThunk(data.client_id || 0)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingContactLoader());
        }
    }
);

export const editClientContactThunk = createAsyncThunk(
    'contact/edit-client',
    async (data: EditContactParams, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await ContactApi.editClientContact(state.app.token, data);
            if (response?.success === 1) {
                dispatch(settingSelectedContact(null));
                await dispatch(fetchClientContactThunk(data.client_id || 0)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingContactLoader());
        }
    }
);

export const deleteClientContactThunk = createAsyncThunk(
    'contact/delete-client',
    async (data: DeleteContactParams, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await ContactApi.deleteClientContact(state.app.token, data);
            if (response?.success === 1) {
                dispatch(settingSelectedContact(null));
                await dispatch(fetchClientContactThunk(data.client_id || 0)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingContactLoader());
        }
    }
);